import { Theme } from '@radix-ui/themes'
import {
  Alert,
  Button,
  List,
  message,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  createClassificationIndicationConfigContextValue,
  DEFAULT_CLASSIFICATION_CONFIG,
  IndicationClassificationConfigContext,
  IndicationItem,
  indicationToEntry,
  RolodexIndication,
  RolodexIndicationContextProvider,
  ToggledIndicationDetailView,
  UpdateIndicationContext,
} from 'features/indicationTriage'
import { computeFfsTriageValidation } from 'features/libra/stepComponents/ffs/computeFfsTriageValidation'
import {
  DEFAULT_FFS_LTA_DETAIL_CARDS,
  normalizeFfsDetailCards,
} from 'features/libra/stepComponents/ffs/ffsDetailCards'
import { useFfsMarkupLayers } from 'features/libra/stepComponents/ffs/useFfsMarkupLayers'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsTriageData } from 'features/libra/stepComponents/ffs/useFfsTriageData'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { LibraWorflowStepComponentProps } from 'features/libra/workflow/workflowStepsApi'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

type Classification = 'pass' | 'fail' | 'derate' | 'pending'

const CLASSIFICATION_TAG_COLORS: Record<Classification, string> = {
  pass: 'green',
  fail: 'red',
  derate: 'gold',
  pending: 'blue',
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

/**
 * Indication triage step. Lists LTA indications with classification controls
 * and pushes ambient FFS markup layers to the workspace viz pane.
 *
 * Differs from the details-page extension: CTPs / rulers are queried per-
 * indication-set (not lazily per detail card) so the user sees full FFS
 * context -- LTA boundaries, thinning-path geometry, measurement rulers -- all
 * on the viz pane throughout triage.
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = ({ readonly = false }) => {
  const readOnly = readonly
  const {
    component,
    componentId,
    inspectionId,
    isLoading: ticketLoading,
  } = useFfsTicketContext()
  const {
    ltaSet,
    indications,
    isLoading: triageDataLoading,
    forceRefresh: refreshTriageIndications,
  } = useFfsTriageData({ component, inspectionId })
  const isLoading = ticketLoading || triageDataLoading

  const { validation, totalCount, triagedCount } = useMemo(
    () =>
      computeFfsTriageValidation({
        component,
        componentId,
        inspectionId,
        ltaSet,
        indications,
        isLoading,
      }),
    [component, componentId, inspectionId, ltaSet, indications, isLoading]
  )

  useReportValidation(validation)

  const [messageApi, messageContextHolder] = message.useMessage()
  const { rolodexPatch, rolodexPut } = useRolodexEdit()
  const {
    selectedIndicationId,
    setSelectedIndicationId,
    primaryCdf,
    setZoomToLayerId,
  } = useFfsWorkspaceState()

  useFfsMarkupLayers({
    indications,
    ltaSetId: ltaSet?.id,
    component,
    inspectionId,
    selectedIndicationId,
  })

  const [detailIndication, setDetailIndication] =
    useState<RolodexIndication | null>(null)
  /** Per-indication in-flight classification patch -- blocks rapid-click races. */
  const [pendingClassifyIds, setPendingClassifyIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  // Drawer <-> viz selection sync. When the user clicks an indication rect in
  // the viz, the panel sets `selectedIndicationId`; we react by opening the
  // matching indication's drawer. When the user closes the drawer or clicks
  // a row directly, we keep the two surfaces aligned via the row's onClick
  // handler below.
  useEffect(() => {
    if (!selectedIndicationId) {
      if (detailIndication) setDetailIndication(null)
      return
    }
    if (detailIndication?.id === selectedIndicationId) return
    const match = indications.find((i) => i.id === selectedIndicationId)
    if (match) setDetailIndication(match)
  }, [selectedIndicationId, indications, detailIndication])

  const classificationFor = (
    indication: RolodexIndication
  ): string | undefined => indication.classificationInfo?.classification

  const classify = async (
    indication: RolodexIndication,
    classification: Classification
  ) => {
    if (readOnly) return
    if (pendingClassifyIds.has(indication.id)) return
    setPendingClassifyIds((prev) => new Set(prev).add(indication.id))
    try {
      // Preserve existing classificationInfo fields (refVal / unitId written
      // by the FFS calc card) -- a bare `{ classification }` patch would
      // wipe them. AND `entryToIndication` parses `classificationInfo` ONLY
      // when `unitId` resolves to a known measurement unit -- so a fresh
      // LTA without prior refVal/unitId requires a default unit, otherwise
      // the classification is silently dropped on the next read.
      const existing = indication.classificationInfo
      const nextClassificationInfo: {
        classification: Classification
        refVal: number
        unitId: string
      } = {
        classification,
        refVal: existing?.refVal?.num ?? 0,
        unitId:
          existing?.refVal?.unit?.id ?? GeckoMeasurementUnit.Millimeters.id,
      }
      await rolodexPatch([
        {
          id: indication.id,
          data: { classificationInfo: nextClassificationInfo },
        },
      ])
      await refreshTriageIndications()
    } catch {
      messageApi.error(`Failed to classify ${indication.id}`)
    } finally {
      setPendingClassifyIds((prev) => {
        if (!prev.has(indication.id)) return prev
        const next = new Set(prev)
        next.delete(indication.id)
        return next
      })
    }
  }

  // Detail-drawer contexts.
  const classificationConfigValue = useMemo(
    () =>
      createClassificationIndicationConfigContextValue(
        DEFAULT_CLASSIFICATION_CONFIG
      ),
    []
  )

  const updateContextValue = useMemo(
    () => ({
      updateOrAddIndication: (item: IndicationItem) => {
        // Detail-drawer cards (`Note`, `FfsCalc`, classification, etc.) all
        // funnel through this single entry point. Gating the top-level
        // classify buttons is not enough — when the ticket is read-only
        // we must also refuse drawer-driven writes, otherwise a card can
        // still mutate the indication on a locked/approved ticket.
        if (readOnly) {
          messageApi.warning(
            'Ticket is read-only; changes were not saved to Rolodex.'
          )
          return
        }
        // Persist the full IndicationItem via indicationToEntry, then layer in
        // classificationInfo (which indicationToEntry doesn't include). The
        // synced-refresh registry picks up the write via refreshIndications.
        const rolodexItem = item as RolodexIndication
        const baseEntry = indicationToEntry(rolodexItem)
        const classificationInfo = rolodexItem.classificationInfo
          ? {
              classification: rolodexItem.classificationInfo.classification,
              refVal: rolodexItem.classificationInfo.refVal?.num,
              unitId: rolodexItem.classificationInfo.refVal?.unit.id,
            }
          : undefined
        void (async () => {
          try {
            await rolodexPut([
              {
                ...baseEntry,
                data: {
                  ...baseEntry.data,
                  ...(classificationInfo ? { classificationInfo } : {}),
                },
              },
            ])
            await refreshTriageIndications()
          } catch {
            messageApi.error(`Failed to update ${rolodexItem.id}`)
          }
        })()
      },
      refreshIndications: async () => {
        await refreshTriageIndications()
      },
    }),
    [messageApi, readOnly, refreshTriageIndications, rolodexPut]
  )

  // Memoized so the detail view's `cardConfig` keeps a stable reference across
  // renders. `normalizeFfsDetailCards` returns a fresh array; calling it inline
  // in JSX makes `useIndicationCards` reload/remount the cards every render,
  // which thrashes the thickness histogram's cropped-CDF creation so its
  // summary stats never settle.
  const resolvedDetailCards = useMemo(
    () =>
      normalizeFfsDetailCards(
        ltaSet?.detailCards?.length
          ? ltaSet.detailCards
          : DEFAULT_FFS_LTA_DETAIL_CARDS
      ),
    [ltaSet?.detailCards]
  )

  // Extension context for the detail cards. Libra has no auxiliary bottom panel
  // (those are FDE-only, no-op here), but it DOES expose the viz zoom: the Ruler
  // card's per-row "zoom to" calls `zoomToRegion`, which parks the ruler id in
  // workspace state for `FfsAssetVizPanel` to frame.
  const detailExtensionContext = useMemo(
    () => ({
      bottomPanelContainer: null,
      requestAuxiliaryBottomPanel: () => {},
      releaseAuxiliaryBottomPanel: () => {},
      zoomToRegion: (regionId: string) => setZoomToLayerId(regionId),
    }),
    [setZoomToLayerId]
  )

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  return (
    <Container>
      {messageContextHolder}
      <Typography.Title level={4}>Indication Triage</Typography.Title>
      <Typography.Paragraph type='secondary'>
        Classify each LTA -- pass, fail, derate, or pending. {triagedCount} of{' '}
        {totalCount} classified. The viz pane shows indication outlines,
        critical thickness profile lines, and rulers in parallel.
      </Typography.Paragraph>

      {!componentId || !inspectionId ? (
        <Alert
          type='error'
          showIcon
          message='Ticket is missing required component or inspection tag.'
        />
      ) : !component ? (
        <Alert
          type='error'
          showIcon
          message='Component entry not found in Rolodex.'
        />
      ) : !ltaSet ? (
        <Alert
          type='warning'
          showIcon
          message='No LTA indication set found yet. Return to the Binning step and Generate Local Thin Areas first.'
        />
      ) : totalCount === 0 ? (
        <Alert
          type='info'
          showIcon
          message={`Indication set "${ltaSet.id}" exists but has no indications.`}
        />
      ) : (
        <List
          size='small'
          bordered
          dataSource={[...indications]}
          renderItem={(indication: RolodexIndication) => {
            const current = classificationFor(indication) as
              | Classification
              | undefined
            const isPending = pendingClassifyIds.has(indication.id)
            return (
              <List.Item>
                <Button
                  type='link'
                  size='small'
                  style={{ flex: 1, textAlign: 'left', padding: 0 }}
                  onClick={() => {
                    setSelectedIndicationId(indication.id)
                    setDetailIndication(indication)
                  }}>
                  {indication.title}
                </Button>
                <Space size='small'>
                  {current ? (
                    <Tag color={CLASSIFICATION_TAG_COLORS[current]}>
                      {current}
                    </Tag>
                  ) : null}
                  {(
                    ['pass', 'fail', 'derate', 'pending'] as Classification[]
                  ).map((cls) => (
                    <Button
                      key={cls}
                      size='small'
                      type={current === cls ? 'primary' : 'default'}
                      danger={cls === 'fail' && current === 'fail'}
                      loading={isPending && current !== cls}
                      disabled={readOnly || isPending}
                      onClick={() => classify(indication, cls)}>
                      {cls}
                    </Button>
                  ))}
                </Space>
              </List.Item>
            )
          }}
        />
      )}

      <Theme asChild hasBackground={false} appearance='inherit'>
        <IndicationClassificationConfigContext.Provider
          value={classificationConfigValue}>
          <UpdateIndicationContext.Provider value={updateContextValue}>
            {detailIndication !== null ? (
              <RolodexIndicationContextProvider
                indicationId={detailIndication.id}
                /* Host-supplied CDF: the shared provider can't resolve a
                   data-bearing CDF itself, so libra hands it the workspace's
                   already-resolved primaryCdf (same contract as the details
                   page's DetailsIndicationProvider). */
                resolvedThicknessCdf={primaryCdf}>
                <ToggledIndicationDetailView
                  isOpen={detailIndication !== null}
                  close={() => {
                    setDetailIndication(null)
                    setSelectedIndicationId(null)
                  }}
                  indicationItem={detailIndication}
                  cardConfig={resolvedDetailCards}
                  extensionContext={detailExtensionContext}
                  type='inline'
                />
              </RolodexIndicationContextProvider>
            ) : null}
          </UpdateIndicationContext.Provider>
        </IndicationClassificationConfigContext.Provider>
      </Theme>
    </Container>
  )
}
